import axios, { AxiosError, AxiosHeaders, AxiosRequestConfig, AxiosResponse, CancelToken, GenericAbortSignal } from './axios';
import { MaybePromise } from './promise';
import { AddParams } from './url';

interface axiosRequestConfig extends AxiosRequestConfig {
    params?: Record<string, unknown>;
}

interface request {
    config: axiosRequestConfig;

    requestID: string;
    promise: Promise<AxiosResponse<unknown>>;
}

declare interface batchRequest {
    requestID: string;
    method: string;
    uri: string;
    headers?: Record<string, string>;
    body?: string;
}

declare interface batchPayload {
    requests: batchRequest[];
    async: boolean;
}

declare interface response {
    requestID: string;
    status: number;
    headers: Record<string, string[]>;
    body: string;
}

export interface BatchOptions {
    async?: boolean;
}

export default class Batch {
    #requests: request[] = [];

    #resolve!: (value: MaybePromise<AxiosResponse<response[]>>) => void;
    #promise = new Promise<AxiosResponse<response[]>>(r => this.#resolve = r);

    #async: boolean;

    #cancelToken?: CancelToken = undefined;
    #signal?: GenericAbortSignal;

    constructor(opts: BatchOptions = {}) {
        this.#async = opts.async ?? true;
    }

    async exec() {
        const batchRequest: batchPayload = {
            requests: this.#requests.map(request => {
                if (!request.config.method) {
                    throw new Error('request method not set');
                }
                if (!request.config.url) {
                    throw new Error('request url not set');
                }

                request.config.headers ??= {};
                for (const k of Object.keys(request.config.headers)) {
                    const v: unknown = request.config.headers[k];
                    delete request.config.headers[k];
                    request.config.headers[k.toLowerCase()] = v;
                }

                const r: batchRequest = {
                    requestID: request.requestID,
                    method: request.config.method,
                    uri: request.config.url,
                };

                if (request.config?.params && typeof request.config.params === 'object' && request.config.params !== null) {
                    r.uri = AddParams(r.uri, request.config.params);
                }

                if (request.config.headers['content-type'] === 'application/json') {
                    r.body = btoa(JSON.stringify(request.config.data));
                }

                return r;
            }),
            async: this.#async,
        };

        if (!batchRequest.requests.length) {
            return;
        }

        const p = axios.post<response[]>('/batch', batchRequest, {
            cancelToken: this.#cancelToken,
            signal: this.#signal,
        });
        // Resolve with the in-flight promise (not its awaited value) so a
        // failed /batch POST rejects every sub-request promise instead of
        // leaving them pending forever.
        this.#resolve(p);
        await p;
        return p;
    }

    #currRequestID = 0;

    request<T>(config: AxiosRequestConfig = {}): Promise<AxiosResponse<T>> {
        config.method ??= axios.defaults.method ?? 'GET';
        config.validateStatus ??= axios.defaults.validateStatus ?? (status => 200 <= status && status <= 299);

        if (config.cancelToken) {
            this.#cancelToken = config.cancelToken;
        }
        if (config.signal) {
            this.#signal = config.signal;
        }

        const requestID = String(this.#currRequestID++);
        const promise = new Promise<AxiosResponse<T>>((resolve, reject) => {
            void this.#promise.then(resp => {
                for (const r of resp.data) {
                    if (r.requestID !== requestID) {
                        continue;
                    }

                    const headers: Record<string, string> = {};
                    for (const k of Object.keys(r.headers)) {
                        headers[k.toLowerCase()] = r.headers[k].join(' ');
                    }

                    let data: unknown;

                    if (r.body) {
                        const body = decodeURIComponent(atob(r.body).split('').map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)).join(''));
                        if (headers['content-type'].startsWith('application/json')) {
                            data = JSON.parse(body);
                        } else {
                            data = body;
                        }
                    }

                    const resp: AxiosResponse = {
                        config: {
                            ...config,
                            headers: new AxiosHeaders(headers),
                        },
                        data,
                        headers,
                        status: r.status,
                        statusText: '',
                    };

                    if (config.validateStatus && !config.validateStatus(r.status)) {
                        const err: AxiosError<T> = {
                            message: `batch request response returned status code ${r.status}`,
                            name: 'AxiosError',
                            config: {
                                ...config,
                                headers: new AxiosHeaders(headers),
                            },
                            isAxiosError: true,
                            response: resp,
                            toJSON: () => ({}),
                        };
                        reject(err);
                        return;
                    }

                    resolve(resp);
                    return;
                }

                throw new Error(`response for requestID "${requestID}" missing in batch response`);
            }).catch((err: unknown) => {
                reject(err instanceof Error ? err : new Error(String(err)));
            });
        });

        this.#requests.push({
            config,
            requestID,
            promise,
        });

        return promise;
    }

    get<T>(url: string, config: AxiosRequestConfig = {}): Promise<AxiosResponse<T>> {
        config.method = 'GET';
        config.url = url;
        return this.request(config);
    }

    post<T>(url: string, data?: unknown, config: AxiosRequestConfig = {}): Promise<AxiosResponse<T>> {
        config.method = 'POST';
        config.url = url;
        config.data = data;
        config.headers ??= {};
        config.headers['content-type'] = 'application/json';
        return this.request(config);
    }
}

export declare interface Getter {
    get<T = unknown>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>>;
}